/**
 * Sticky running-margin strip — lines sum to engine projected_cost (no back-solve).
 */
import { formatMoney } from '../../../../lib/formatters'
import {
  buildMarginStripLines,
  formatDeductionCents,
  stripLinesSumCents,
} from '../../../../lib/costsMarginStrip'
import type { CostsMarginPayload, JobCostRow } from '../../../../api/costsMargin'
import { brand } from '../../../../config/brand'

function money(m: { amount?: number; amount_cents?: number; formatted?: string } | undefined) {
  if (!m) return formatMoney(0)
  if (m.formatted) return m.formatted
  if (m.amount_cents != null) return formatMoney(m.amount_cents / 100)
  return formatMoney(m.amount ?? 0)
}

export default function MarginStrip({
  data,
  costs = [],
}: {
  data: CostsMarginPayload | undefined
  costs?: JobCostRow[]
}) {
  const pct =
    data?.projected_margin_pct != null && Number.isFinite(data.projected_margin_pct)
      ? `${Number(data.projected_margin_pct).toFixed(1)}%`
      : '—'
  const completeness =
    data?.completeness_line
    || 'Nothing estimated. Enter costs to see projected margin.'
  const peak = data?.peak_out_of_pocket?.peak
  const lines = buildMarginStripLines(data?.commitments ?? [], costs)
  const lineSum = stripLinesSumCents(lines)
  const projectedCents = data?.projected_cost?.amount_cents ?? 0

  return (
    <section className="cm-margin-card" aria-labelledby="cm-margin-title" data-testid="cm-margin-strip">
      <div className="cm-card-head">
        <div className="cm-eyebrow">Running margin</div>
        <h2 id="cm-margin-title">What this job makes so far</h2>
        <p>Updates only from costs you enter. {brand.name} does not estimate.</p>
      </div>
      <div className="cm-margin-body">
        <div className="cm-margin-status">
          <span>Projected margin</span>
          <span className="cm-margin-percent" data-testid="cm-margin-pct">{pct}</span>
        </div>
        <div className="cm-math-row">
          <span>Revised contract</span>
          <span className="money">{money(data?.revised_contract)}</span>
        </div>
        {lines.map((line) => (
          <div
            key={line.id}
            className="cm-math-row cm-deduction"
            data-testid={`cm-strip-line-${line.id}`}
          >
            <span>{line.label}</span>
            <span className="money" data-testid={line.id === 'direct' ? 'cm-direct-costs' : undefined}>
              {formatDeductionCents(line.amount_cents)}
            </span>
          </div>
        ))}
        <div className="cm-math-row" data-testid="cm-strip-sum-check" data-sum={lineSum} data-projected={projectedCents}>
          <span>Projected cost</span>
          <span className="money">{money(data?.projected_cost)}</span>
        </div>
        <div className="cm-margin-total">
          <span>Projected margin so far</span>
          <strong className="money" data-testid="cm-margin-amount">
            {money(data?.projected_margin)}
          </strong>
        </div>
        <div className="cm-completeness" data-testid="cm-completeness">
          <b>{completeness}</b>
        </div>
        <div className="cm-math-row" style={{ marginTop: 10 }}>
          <span>Peak out-of-pocket</span>
          <span className="money" data-testid="cm-peak-oop">{money(peak)}</span>
        </div>
        <div className="cm-rule">
          <span className="cm-rule-mark">1</span>
          <span>
            <b>Subcontract Amounts count now.</b> Full agreed amounts reduce margin even before invoices arrive — when invoiced is higher, that higher figure is used.
          </span>
        </div>
        <div className="cm-rule">
          <span className="cm-rule-mark">2</span>
          <span>
            <b>Nothing is estimated.</b> No guessed cost, markup, or percent complete is included.
          </span>
        </div>
      </div>
    </section>
  )
}
